import { useState, useEffect } from 'preact/hooks'
import { apiClient, InitData, tenantSiteUrl, assetUrl, udLink } from '../../core'
import { t, intlLocale } from '../../core/i18n'
import { fmtDag, studietsTidszone } from '../../core/tidszone'

interface BlogPost {
  id: string
  title: string
  slug: string | null
  excerpt: string | null
  featuredImageUrl: string | null
  publishedAt: string
  authorName: string | null
}

interface Props {
  initData: InitData
  limit?: number | null
  showImages?: boolean
}

// Publiceringstidspunktet i studiets tidszone, ikke den besøgendes browser (QA 27-10).
function formatDate(dateStr: string, locale: string, timeZone: string): string {
  return fmtDag(dateStr, locale, timeZone, { day: 'numeric', month: 'long', year: 'numeric' })
}

/**
 * BlogPostsWidget — mirrors HS-native blog list styling.
 *
 * Card layout:
 *   - Optional featured image (16:9, cover)
 *   - Date (uppercase, muted)
 *   - Title (bold, 1.25rem)
 *   - Excerpt (gray, 0.95rem, line-clamp 3)
 *   - "Læs mere" link (orange) at bottom
 */
export function BlogPostsWidget({ initData, limit = null, showImages = true }: Props) {
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetch() {
      // limit SKAL med i kaldet: API'et returnerer ellers kun sine 6 standard-
      // indlæg, og limit="12" viste stadig 6. Uden limit beholder vi API'ets standard.
      const query = limit && limit > 0 ? `?limit=${limit}` : ''
      const result = await apiClient.get<BlogPost[]>(`/api/widget/blog${query}`)
      if (result.data) setPosts(result.data)
      if (result.error) setError(result.error)
      setLoading(false)
    }
    fetch()
  }, [limit])

  if (loading) {
    // Skeleton loading — 3 placeholder cards
    return (
      <div>
        <style>{skeletonStyles}</style>
        <div class="hsw-blog-grid">
          {[0, 1, 2].map(i => (
            <div key={i} class="hsw-blog-skel-card">
              <div class="hsw-blog-skel hsw-blog-sk-image" />
              <div class="hsw-blog-skel hsw-blog-sk-date" />
              <div class="hsw-blog-skel hsw-blog-sk-title" />
              <div class="hsw-blog-skel hsw-blog-sk-line" />
              <div class="hsw-blog-skel hsw-blog-sk-line" />
              <div class="hsw-blog-skel hsw-blog-sk-line" style={{ width: '60%' }} />
              <div class="hsw-blog-skel hsw-blog-sk-link" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <style>{blogStyles}</style>
        <div role="alert" style={{ padding: '1rem 1.5rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--yoga-radius, 0)', color: '#b91c1c', maxWidth: '600px', margin: '0 auto' }}>
          <strong>⚠️ {error}</strong>
        </div>
      </div>
    )
  }

  const visiblePosts = limit && limit > 0 ? posts.slice(0, limit) : posts

  if (!visiblePosts.length) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#737373' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📝</div>
        <p>{t(initData.locale).noBlogPosts}</p>
      </div>
    )
  }

  return (
    <div>
      <style>{blogStyles}</style>
      <div class="hsw-blog-grid">
        {visiblePosts.map(post => {
          const href = post.slug
            ? `${tenantSiteUrl(initData)}/blog/${post.slug}`
            : null
          return (
            <article key={post.id} class="hsw-blog-card">
              {showImages && post.featuredImageUrl && (
                <div class="hsw-blog-image-wrap">
                  {href ? (
                    <a href={href} {...udLink(href)} class="hsw-blog-image-link" aria-label={post.title}>
                      <img src={assetUrl(post.featuredImageUrl)} alt={post.title} class="hsw-blog-image" />
                    </a>
                  ) : (
                    <img src={assetUrl(post.featuredImageUrl)} alt={post.title} class="hsw-blog-image" />
                  )}
                </div>
              )}
              <div class="hsw-blog-body">
                <div class="hsw-blog-date">{formatDate(post.publishedAt, intlLocale(initData.locale), studietsTidszone(initData.timezone))}</div>
                <h3 class="hsw-blog-title">
                  {href ? (
                    <a href={href} {...udLink(href)} class="hsw-blog-title-link">{post.title}</a>
                  ) : (
                    post.title
                  )}
                </h3>
                {post.excerpt && (
                  <p class="hsw-blog-excerpt">{post.excerpt}</p>
                )}
                {href && (
                  <a href={href} {...udLink(href)} class="hsw-blog-readmore">
                    {t(initData.locale).readMore} <span aria-hidden="true">→</span>
                  </a>
                )}
              </div>
            </article>
          )
        })}
      </div>
    </div>
  )
}

const blogStyles = `
  .hsw-blog-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
    max-width: 1100px;
    margin: 0 auto;
  }
  .hsw-blog-card {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    overflow: hidden;
    transition: all .3s;
    display: flex;
    flex-direction: column;
  }
  .hsw-blog-card:hover {
    border-color: rgba(0,0,0,0.2);
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
    transform: translateY(-2px);
  }
  .hsw-blog-image-wrap {
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    background: #f5f5f5;
  }
  .hsw-blog-image-link {
    display: block;
    width: 100%;
    height: 100%;
  }
  .hsw-blog-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform .4s ease;
  }
  .hsw-blog-card:hover .hsw-blog-image {
    transform: scale(1.04);
  }
  .hsw-blog-body {
    padding: 2rem;
    display: flex;
    flex-direction: column;
    flex: 1;
  }
  .hsw-blog-date {
    font-size: 0.75rem;
    color: var(--yoga-text-muted, #737373);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 500;
    margin-bottom: 0.6rem;
  }
  .hsw-blog-title {
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0 0 0.75rem;
    color: var(--yoga-text, #0a0a0a);
    line-height: 1.3;
    letter-spacing: -0.01em;
  }
  .hsw-blog-title-link {
    color: inherit;
    text-decoration: none;
    transition: color .2s;
  }
  .hsw-blog-title-link:hover {
    color: var(--yoga-primary, #FF6B35);
  }
  .hsw-blog-excerpt {
    font-size: 0.95rem;
    color: var(--yoga-text-muted, #525252);
    line-height: 1.6;
    margin: 0 0 1.25rem;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .hsw-blog-readmore {
    margin-top: auto;
    color: var(--yoga-primary, #FF6B35);
    font-size: 0.9rem;
    font-weight: 600;
    text-decoration: none;
    letter-spacing: 0.01em;
    align-self: flex-start;
    transition: gap .2s;
  }
  .hsw-blog-readmore:hover {
    text-decoration: underline;
  }
`

const skeletonStyles = `
  .hsw-blog-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
    max-width: 1100px;
    margin: 0 auto;
  }
  .hsw-blog-skel-card {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    overflow: hidden;
  }
  .hsw-blog-skel {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: hsw-shimmer 1.5s infinite;
    border-radius: 4px;
  }
  .hsw-blog-sk-image { width: 100%; aspect-ratio: 16 / 9; border-radius: 0; margin-bottom: 0; }
  .hsw-blog-sk-date { height: 12px; width: 35%; margin: 2rem 2rem 0.75rem; }
  .hsw-blog-sk-title { height: 22px; width: 80%; margin: 0 2rem 1rem; }
  .hsw-blog-sk-line { height: 14px; width: calc(100% - 4rem); margin: 0 2rem 0.6rem; }
  .hsw-blog-sk-link { height: 16px; width: 30%; margin: 0.75rem 2rem 2rem; }
  @keyframes hsw-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`
